import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from 'src/components/Modal';
import PreviewPDF from 'src/components/PreviewPDF';
import Button from 'src/components/design-system/Button';
import useGetEdiUserManualApi from 'src/services/edi_admin_tools/useGetEdiUserManualApi';
import { validSignedUrl } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import { EdiAdminContext } from './EdiAdminContext';

interface AdminToolsHeaderProps {}

const AdminToolsHeader: FC<AdminToolsHeaderProps> = () => {
  const { t } = useTranslation();
  const selectedView = useContextSelector(EdiAdminContext, (ctx) => ctx.selectedView);
  const setSelectedView = useContextSelector(EdiAdminContext, (ctx) => ctx.setSelectedView);
  const exportCurrentViewLogs = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.exportCurrentViewLogs
  );
  const isExportLogsPending = useContextSelector(EdiAdminContext, (ctx) => ctx.isExportLogsPending);
  const hasCurrentViewRecords = useContextSelector(EdiAdminContext, (ctx) =>
    ctx.selectedView === 0 ? ctx.timingLogsTotal > 0 : ctx.outstanding810GridData.length > 0
  );
  const { getUserManualExecute, getUserManualLoading } = useGetEdiUserManualApi();

  const [isUserManualOpen, setIsUserManualOpen] = useState(false);
  const [manualUrl, setManualUrl] = useState('');

  const handleCloseManualModal = useCallback(() => {
    setIsUserManualOpen(false);
  }, []);

  const handleUserManualClick = useCallback(async () => {
    try {
      const { data } = await getUserManualExecute();
      setManualUrl(validSignedUrl(data?.downloadUrl || ''));
      setIsUserManualOpen(true);
    } catch {
      setManualUrl('');
      toast.error('Unable to load user manual.');
    }
  }, [getUserManualExecute]);

  return (
    <>
      <div className="flex items-center justify-between gap-4 mt-4 mb-2 flex-wrap px-4 w-full">
        <TabStrip
          className="min-w-[190px] flex-shrink-0"
          selected={selectedView}
          onSelect={(e) => setSelectedView(e.selected)}
        >
          <TabStripTab contentClassName="!hidden" title={t('Timing logs')} />
          <TabStripTab contentClassName="!hidden" title={t('Outstanding 810 logs')} />
        </TabStrip>
        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <Button
            color="secondary"
            onClick={() => void exportCurrentViewLogs()}
            disabled={isExportLogsPending || !hasCurrentViewRecords}
            loading={isExportLogsPending}
          >
            {t('exportToExcel')}
          </Button>
          <Button
            color="secondary"
            onClick={() => void handleUserManualClick()}
            loading={getUserManualLoading}
            disabled={getUserManualLoading}
          >
            {t('User Manual')}
          </Button>
        </div>
      </div>
      <Modal
        visible={isUserManualOpen}
        closeModal={handleCloseManualModal}
        title={t('User Manual')}
        width="60vw"
        height="94vh"
        className="[&_.k-dialog-content]:flex [&_.k-dialog-content]:flex-col [&_.k-dialog-content]:min-h-0 [&_.k-dialog-content]:overflow-hidden"
      >
        <div className="flex flex-col gap-3 min-h-0 w-full h-full max-h-full">
          <div className="flex-1 min-h-0 min-w-0 w-full overflow-hidden">
            <PreviewPDF url={manualUrl} height="100%" filename="User_manual" />
          </div>
          <div className="flex justify-end shrink-0">
            <Button color="info" onClick={handleCloseManualModal}>
              {t('Close')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AdminToolsHeader;
